import React, { useRef } from 'react';
import { weatherLayers } from '../../../const/weatherLayer';
import styled from 'styled-components';
import 'leaflet-velocity/dist/leaflet-velocity';
import '@kngai/leaflet.timezones';
import { Checkbox, Image, Spin } from 'antd';
import useMapSetting, { EMapType } from '../../../zustand/useMap';
import { capitalizeFirstLetter } from '@qlibs/react-components';
import MapSatellite from '../../../assets/icons/home/map-satellite.png';
import MapVector from '../../../assets/icons/home/map-vector.png';
import MapVectorWhite from '../../../assets/icons/home/map-vector-white.png';
import { LoadingOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';

type MapOverlayListProps = {
  compact?: boolean;
};

const MapOverlayList: React.FC<MapOverlayListProps> = ({
  compact = false,
}) => {
  const {
    activeLayer,
    setActiveLayer,
    showWindLayer,
    setShowWindLayer,
    showWaveLayer,
    setShowWaveLayer,
    setShowTimezoneLayer,
    showTimezoneLayer,
    loadingWindDirection,
    loadingWaveDirection,
  } = useMapSetting();

  const { pathname } = useLocation();

  const handleToggleTimezone = () => {
    setShowTimezoneLayer(!showTimezoneLayer);
  };

  const handleToggleWindLayer = () => {
    setShowWindLayer(!showWindLayer);
  };

  const handleToggleWaveLayer = () => {
    setShowWaveLayer(!showWaveLayer);
  };

  const handleToggleActiveLayer = (overlayName: string) => {
    if (activeLayer === overlayName) {
      setActiveLayer('');
    } else {
      setActiveLayer(overlayName);
    }
  };

  const yesterdayDate = dayjs().subtract(1, 'day').format('YYYYMMDD');

  return (
    <div style={{ height: 'auto' }}>
      <WeatherLayerOverlayControl
        isCustomLayout={!pathname.includes('/home')}
        loadingWindDirection={loadingWindDirection}
        loadingWaveDirection={loadingWaveDirection}
        activeLayer={activeLayer}
        showTimezoneLayer={showTimezoneLayer}
        showWindLayer={showWindLayer}
        showWaveLayer={showWaveLayer}
        forecastDate={yesterdayDate}
        compact={compact}
        onToggleActiveLayer={handleToggleActiveLayer}
        onToggleTimezone={handleToggleTimezone}
        onToggleWindLayer={handleToggleWindLayer}
        onToggleWaveLayer={handleToggleWaveLayer}
      />
    </div>
  );
};

const WeatherLayerOverlayControl: React.FC<{
  onToggleActiveLayer: (overlayName: string) => void;
  onToggleWindLayer: () => void;
  onToggleWaveLayer: () => void;
  onToggleTimezone: () => void;
  activeLayer: string;
  showTimezoneLayer: boolean;
  showWindLayer: boolean;
  showWaveLayer: boolean;
  loadingWindDirection: boolean;
  loadingWaveDirection: boolean;
  forecastDate: string;
  isCustomLayout?: boolean;
  compact?: boolean;
}> = ({
  onToggleActiveLayer,
  onToggleWindLayer,
  onToggleWaveLayer,
  onToggleTimezone,
  activeLayer,
  loadingWindDirection,
  loadingWaveDirection,
  showTimezoneLayer,
  showWindLayer,
  showWaveLayer,
  forecastDate,
  isCustomLayout,
  compact,
}) => {
  const controlRef = useRef<HTMLDivElement>(null);

  return (
    <ContainerLayerOverlayControl
      ref={controlRef}
      customLayout={isCustomLayout}
      $compact={compact}
    >


      {Object.keys(weatherLayers).map((key) => {
        const label = key.split('_').join(' ');
        return (
          <ButtonLayerControl
            active={activeLayer === key}
            $compact={compact}
            key={key}
            onClick={() => onToggleActiveLayer(key)}
          >
            <Checkbox checked={activeLayer === key} />
            <span style={{ marginLeft: compact ? 6 : 10 }}>{label}</span>
          </ButtonLayerControl>
        );
      })}

      <DividerLayer />

      <ButtonLayerControl
        active={showWindLayer}
        $compact={compact}
        onClick={() => onToggleWindLayer()}
      >
        <Checkbox checked={showWindLayer} />
        <span
          style={{ marginLeft: compact ? 6 : 10, marginRight: compact ? 6 : 10 }}
        >
          Wind Direction
        </span>
        {loadingWindDirection && (
          <Spin
            indicator={<LoadingOutlined style={{ color: '#132b68' }} spin />}
            size='small'
          />
        )}
      </ButtonLayerControl>

      <ButtonLayerControl
        active={showWaveLayer}
        $compact={compact}
        onClick={() => onToggleWaveLayer()}
      >
        <Checkbox checked={showWaveLayer} />
        <span
          style={{ marginLeft: compact ? 6 : 10, marginRight: compact ? 6 : 10 }}
        >
          Wave Direction
        </span>
        {loadingWaveDirection && (
          <Spin
            indicator={<LoadingOutlined style={{ color: '#132b68' }} spin />}
            size='small'
          />
        )}
      </ButtonLayerControl>

      <DividerLayer />

      <ButtonLayerControl
        active={showTimezoneLayer}
        $compact={compact}
        onClick={() => onToggleTimezone()}
      >
        <Checkbox checked={showTimezoneLayer} />
        <span style={{ marginLeft: compact ? 6 : 10 }}>Timezone</span>
      </ButtonLayerControl>

      <DividerLayer />

      <RenderMapSetting
        compact={compact}
        icon={MapSatellite}
        label={EMapType.satellite}
      />
      <RenderMapSetting
        compact={compact}
        icon={MapVector}
        label={EMapType.vector_color}
      />
      <RenderMapSetting
        compact={compact}
        icon={MapVectorWhite}
        label={EMapType.vector_white}
      />
    </ContainerLayerOverlayControl>
  );
};

const RenderMapSetting: React.FC<{
  icon: any;
  label: EMapType;
  compact?: boolean;
}> = ({ icon, label, compact }) => {
  const { setMapType, mapType: value } = useMapSetting();

  return (
    <div
      style={{
        cursor: 'pointer',
        display: 'flex',
        // flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        margin: compact ? '4px 6px' : '5px 10px',
        gap: compact ? 6 : 10,
      }}
      onClick={() => setMapType(label)}
    >
      <Image
        preview={false}
        fallback='/images/empty-image.png'
        src={icon}
        width={compact ? 28 : 35}
        height={compact ? 28 : 35}
        style={{
          borderRadius: 8,
          padding: 2,
          border:
            label === value
              ? '2px solid #132b68'
              : '1px solid rgba(255,255,255,0.3)',
        }}
      />
      <div style={{ fontSize: compact ? 11 : 14 }}>
        {capitalizeFirstLetter(label.split('_').join(' '))}
      </div>
    </div>
  );
};

export default MapOverlayList;

const DividerLayer = styled.div`
  width: 100%;
  height: 1px;
  opacity: 0.2;
  background: rgba(0, 0, 0, 0.6);
  margin: 5px 0;
`;

const ButtonLayerControl = styled.button<{
  active: boolean;
  $compact?: boolean;
}>`
  padding: ${({ $compact }) => ($compact ? '3px 6px' : '5px 10px')};
  font-weight: 700;
  border-radius: 4px;
  width: auto;
  text-align: left;
  display: flex;
  align-items: center;
  font-weight: 400;
  font-size: ${({ $compact }) => ($compact ? '11px' : '14px')};
`;

type ContainerLayerOverlayControlProps = {
  customLayout?: boolean;
  $compact?: boolean;
};

const ContainerLayerOverlayControl = styled.div<ContainerLayerOverlayControlProps>`
  padding-left: 10;
  padding-right: 10;
  height: 200;
  overflow: auto;
  display: ${(props) => (props.customLayout ? 'flex' : 'block')};
  flex-wrap: wrap;

  @media (max-width: 767px) {
    display: block;
    height: auto;
    padding: 4px 0 10px;

    ${ButtonLayerControl} {
      width: 100%;
      min-height: ${({ $compact }) => ($compact ? '28px' : '34px')};
      padding: ${({ $compact }) => ($compact ? '3px 6px' : '4px 6px')};
    }
  }
`;
